import { useEffect } from 'react';
import { apiClient } from './api.client';

/**
 * Component that initializes the ApiClient with the app's token provider
 * Pass the tokenProvider as a prop since it may not be available via React context inside <Admin>
 * 
 * This follows the same pattern as useApiConfig hook for consistency
 * 
 * Usage:
 * ```tsx
 * <Admin ...>
 *   <ApiClientInitializer tokenProvider={appContext.tokenProvider} />
 *   <Resource ... />
 * </Admin>
 * ```
 */
interface ApiClientInitializerProps {
  tokenProvider?: {
    getIdToken: () => Promise<string>;
  };
}

export const ApiClientInitializer = ({ tokenProvider }: ApiClientInitializerProps) => {
  console.log('🔐 [ApiClientInitializer] COMPONENT RENDER - tokenProvider:', !!tokenProvider);
  
  useEffect(() => {
    console.log('🔐 [ApiClientInitializer] useEffect FIRED', {
      hasTokenProvider: !!tokenProvider,
      tokenProviderType: typeof tokenProvider,
      hasGetIdToken: tokenProvider && typeof tokenProvider.getIdToken === 'function',
    });

    // Configure ApiClient with auth token getter (same pattern as useApiConfig)
    if (tokenProvider) {
      console.log('🔐 [ApiClientInitializer] TokenProvider found, configuring apiClient...');
      
      apiClient.setAuthProvider(async () => {
        try {
          console.log('🔐 [ApiClientInitializer] getAuthToken callback invoked');
          
          // Use the app's token provider (consistent with useApiConfig and BackendAnalyticsService)
          const token = await tokenProvider.getIdToken();
          
          if (token) {
            console.log('🔐 [ApiClientInitializer] Retrieved token from tokenProvider (length:', token.length, ')');
            return token;
          }
          
          console.warn('🔐 [ApiClientInitializer] Token provider returned empty token');
          return '';
        } catch (error) {
          console.error('🔐 [ApiClientInitializer] Failed to get auth token from token provider:', error);
          
          // Fallback: try localStorage/sessionStorage (for backwards compatibility)
          const fallbackToken = localStorage.getItem('authToken') || sessionStorage.getItem('authToken') || '';
          if (fallbackToken) {
            console.log('🔐 [ApiClientInitializer] Using fallback token from storage');
          }
          return fallbackToken;
        }
      });
      
      console.log('✅ [ApiClientInitializer] ApiClient configured with tokenProvider (consistent with useApiConfig)');
    } else {
      console.warn('⚠️ [ApiClientInitializer] No tokenProvider prop provided yet');
    }
  }, [tokenProvider]);

  // This component doesn't render anything
  return null;
};

